import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, CheckCircle, Clock, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DealTimeline({ leadId }) {
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/deals/${leadId}/timeline`).then(r => setTimeline(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [leadId]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gold-dark" /></div>;

  const events = timeline?.timeline || [];
  const completed = events.filter(e => e.status === "completed").length;
  const pending = events.filter(e => e.status === "pending").length;

  const statusStyles = {
    completed: {
      ring: "border-emerald-400 bg-emerald-100",
      icon: <CheckCircle className="h-4 w-4 text-emerald-600" />,
      textColor: "text-[#0A0A0A]",
    },
    info: {
      ring: "border-blue-300 bg-blue-50",
      icon: <Sparkles className="h-3.5 w-3.5 text-blue-500" />,
      textColor: "text-blue-700",
    },
    pending: {
      ring: "border-[#e8e0d0] bg-[#faf7f2]",
      icon: <Clock className="h-4 w-4 text-[#bbb]" />,
      textColor: "text-[#999]",
    },
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-1" data-testid="timeline-tab">
      <div className="text-sm text-[#555] mb-4">
        {completed} completed{pending > 0 ? ` · ${pending} upcoming` : ""}
      </div>
      <div className="relative">
        <div className="absolute left-[15px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-emerald-300/40 via-[#e8e0d0] to-[#e8e0d0]/30" />
        {events.map((ev, i) => {
          const s = statusStyles[ev.status] || statusStyles.pending;
          return (
            <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
              className="relative flex gap-4 pb-4">
              <div className={`relative z-10 mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${s.ring}`}>
                {s.icon}
              </div>
              <div className="flex-1 min-w-0 pt-0.5">
                <div className={`text-sm font-medium ${s.textColor}`}>
                  {ev.event}
                  {ev.status === "info" && (
                    <span className="ml-1.5 px-1.5 py-0.5 rounded text-[8px] font-bold bg-blue-50 text-blue-500 border border-blue-200">AUTO</span>
                  )}
                </div>
                {ev.description && ev.description !== "Pending" && (
                  <div className="text-xs text-[#888] mt-0.5">{ev.description}</div>
                )}
                {ev.timestamp && ev.status !== "pending" && (
                  <div className="text-[10px] text-[#bbb] mt-0.5">{new Date(ev.timestamp).toLocaleString()}</div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}
